import React, { useState, useMemo, useEffect, useRef } from 'react';
import { AssetClassManager } from './AssetClassManager';
import { Plus, Search, Edit2, Trash2, ArrowUpRight, ArrowDownRight, TrendingDown, CalendarDays, ChevronDown, ChevronUp, ArrowUp, ArrowDown, ChevronsUpDown, Layers, RefreshCw } from 'lucide-react';
import { useAppState } from '../store/AppContext';
import { Card } from './ui/Card';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import { Modal } from './ui/Modal';
import { Input, Select, Textarea } from './ui/Input';
import { Asset, IncomeEvent, AssetClassMeta } from '../types';
import { isUUID } from '../utils/storage';
import { getAssetCurrentValue, getAssetUnrealizedGain, getAssetUnrealizedGainPercent, getAssetAnnualIncome, getAssetCapitalGainIncome, resolveClassColor, CASH_WALLET_ASSET_ID } from '../utils/calculations';
import { formatVND, formatNumber } from '../utils/format';
import { Money, SensitiveValue } from './ui/SensitiveValue';
import { v4 } from '../utils/uuid';
import { exportAssetsToExcel, parseAssetsFromExcel, mergeImportRows } from '../utils/assetExcel';
import { Download, Upload } from 'lucide-react';
import { AssetTransactionHistory } from './TransactionLogger';
import { HoldingsTable } from './HoldingsTable';
import { fetchAllPrices } from '../utils/priceUpdateService';

const MONTHS_VI = ['Tháng 1','Tháng 2','Tháng 3','Tháng 4','Tháng 5','Tháng 6','Tháng 7','Tháng 8','Tháng 9','Tháng 10','Tháng 11','Tháng 12'];

// Chuyển tiếng Việt có dấu → slug (VD: "Đất Cam Ranh 1" → "dat-cam-ranh-1")
function generateIdFromName(name: string): string {
  return name
    .replace(/[đĐ]/g, 'd')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
}

const emptyAsset = (): Partial<Asset> => ({
  name: '', code: '', assetClass: 'Cổ phiếu',
  purchaseDate: new Date().toISOString().split('T')[0],
  costPrice: 0, quantity: 1, currentPrice: 0,
  currency: 'VND', bucketId: 'ruong-chua', notes: '',
  yieldRate: 0, estimatedIncome: 0, incomeEvents: [],
  capitalGainRate: 0, isComposite: false,
});

interface SellForm { quantity: number; price: number; date: string; notes: string }

const TH = (txt: string, align: 'left'|'right' = 'left'): React.CSSProperties => ({
  padding: '10px 16px', fontSize: 11, fontWeight: 500, color: 'var(--tx-4)',
  textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: align,
});

export function AssetManager({ onNavigateToTx }: { onNavigateToTx?: (txId: string) => void } = {}) {
  const { state, dispatch } = useAppState();
  const { assets, buckets, assetClasses } = state;

  const [search, setSearch] = useState('');
  const [filterClass, setFilterClass] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [editAsset, setEditAsset] = useState<Asset | null>(null);
  const [sellAsset, setSellAsset] = useState<Asset | null>(null);
  const [form, setForm] = useState<Partial<Asset>>(emptyAsset());
  const [sellForm, setSellForm] = useState<SellForm>({ quantity: 0, price: 0, date: new Date().toISOString().split('T')[0], notes: '' });
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [filterBucket, setFilterBucket] = useState('');
  const [codeEdited, setCodeEdited] = useState(false);
  const [priceLinked, setPriceLinked] = useState(true);   // currentPrice bám theo costPrice khi true
  const [classManagerOpen, setClassManagerOpen] = useState(false);
  const [sortKey, setSortKey] = useState<'name'|'assetClass'|'bucket'|'costPrice'|'value'|'gain'|'income'|'capgain'|'totalreturn'>('value');
  const [sortDir, setSortDir] = useState<'asc'|'desc'>('desc');
  const [incomeManuallySet, setIncomeManuallySet] = useState(false);
  const [syncedAt, setSyncedAt] = useState<Date | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const [expandedHoldings, setExpandedHoldings] = useState<string | null>(null);
  const [refreshingAsset, setRefreshingAsset] = useState<string | null>(null);  // assetId being refreshed
  const [refreshingAll, setRefreshingAll] = useState(false);
  const importRef = useRef<HTMLInputElement>(null);
  const tableWrapRef = useRef<HTMLDivElement>(null);
  const didSync = useRef(false);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Gán class is-scrollable khi bảng bị overflow ngang (tránh artifact ::after)
  useEffect(() => {
    const el = tableWrapRef.current;
    if (!el) return;
    const check = () => el.classList.toggle('is-scrollable', el.scrollWidth > el.clientWidth);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [assets.length, filterClass, filterBucket, search]);

  useEffect(() => {
    if (assets.length > 0 && !didSync.current) {
      didSync.current = true;
      setSyncedAt(new Date());
    }
  }, [assets.length]);

  const showToast = (msg: string) => {
    setToast(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3000);
  };

  const handleExport = () => {
    exportAssetsToExcel(assets, buckets);
    showToast(`Đã xuất ${assets.length} tài sản ra file Excel`);
  };

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';   // reset để có thể chọn lại cùng file
    setImporting(true);
    setImportErrors([]);
    try {
      const { rows, errors } = await parseAssetsFromExcel(file);
      if (errors.length > 0) {
        setImportErrors(errors.map(er => er.message));
        setImporting(false);
        return;
      }
      if (rows.length === 0) {
        showToast('File không có dữ liệu hợp lệ');
        setImporting(false);
        return;
      }
      const { updated, inserted, nextAssets } = mergeImportRows(rows, assets, buckets, assetClasses, v4);
      // Ghi toàn bộ danh sách vào store
      nextAssets.forEach(na => {
        const exists = assets.find(a => a.id === na.id);
        if (exists) {
          dispatch({ type: 'UPDATE_ASSET', payload: na });
        } else {
          dispatch({ type: 'ADD_ASSET', payload: na });
        }
      });
      showToast(`Hoàn tất: cập nhật ${updated} tài sản, thêm mới ${inserted} tài sản`);
      setSyncedAt(new Date());
    } catch (err) {
      setImportErrors(['Không thể đọc file. Vui lòng kiểm tra định dạng Excel.']);
    } finally {
      setImporting(false);
    }
  };

  const handleRefreshAssetHoldings = async (assetId: string) => {
    const { holdings } = state;
    const assetHoldings = holdings.filter(h => h.assetId === assetId);
    if (!assetHoldings.length) return;
    setRefreshingAsset(assetId);
    try {
      const results = await fetchAllPrices([], () => {}, assetHoldings);
      if (results.length > 0) {
        dispatch({ type: 'ROLLUP_COMPOSITE_ASSET', payload: { assetId } });
      }
      showToast(`Đã cập nhật giá ${assetHoldings.length} vị thế`);
    } catch {
      showToast('Không thể cập nhật giá vị thế');
    } finally {
      setRefreshingAsset(null);
    }
  };

  const handleRefreshAllHoldings = async () => {
    const { holdings, assets: allAssets } = state;
    if (!holdings.length) return;
    setRefreshingAll(true);
    try {
      const results = await fetchAllPrices(allAssets, () => {}, holdings);
      if (results.length > 0) {
        const compositeIds = Array.from(new Set(holdings.map(h => h.assetId)));
        compositeIds.forEach(assetId => {
          dispatch({ type: 'ROLLUP_COMPOSITE_ASSET', payload: { assetId } });
        });
      }
      showToast(`Đã cập nhật giá ${holdings.length} vị thế`);
    } catch {
      showToast('Không thể cập nhật giá vị thế');
    } finally {
      setRefreshingAll(false);
    }
  };

  const handleQuickAssign = (asset: Asset, newBucketId: string) => {
    const effectiveId = newBucketId || 'ruong-chua';
    const bucket = buckets.find(b => b.id === effectiveId);
    dispatch({ type: 'UPDATE_ASSET', payload: { ...asset, bucketId: effectiveId } });
    showToast(`Đã chuyển "${asset.name}" vào ${bucket?.name ?? 'Chưa phân loại'}`);
  };

  const handleQuickAssignClass = (asset: Asset, newClassId: string) => {
    const cls = assetClasses.find(c => c.id === newClassId);
    if (!cls) return;

    let targetId   = cls.id;
    let targetName = cls.name;

    if (!isUUID(cls.id)) {
      // Slug class không sync được lên DB — cần promote sang UUID
      const existing = assetClasses.find(c => isUUID(c.id) && c.name === cls.name);
      if (existing) {
        // Đã có UUID version cùng tên → dùng luôn
        targetId = existing.id;
      } else {
        // Tạo UUID mới và thay thế slug trong toàn bộ state
        const newId = v4();
        const newClass: AssetClassMeta = { ...cls, id: newId };
        dispatch({ type: 'PROMOTE_ASSET_CLASS', payload: { oldId: cls.id, newClass } });
        targetId = newId;
      }
    }

    dispatch({ type: 'UPDATE_ASSET', payload: { ...asset, assetClassId: targetId, assetClass: targetName } });
    showToast(`Đã đổi loại "${asset.name}" → ${targetName}`);
  };

  const handleSort = (key: typeof sortKey) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  // Ví chung hiển thị riêng (pinned row) — loại khỏi bảng thông thường
  const walletAsset = useMemo(() => assets.find(a => a.id === CASH_WALLET_ASSET_ID), [assets]);

  const filtered = useMemo(() =>
    assets.filter(a => {
      if (a.id === CASH_WALLET_ASSET_ID) return false;   // ví hiển thị riêng
      const q = search.toLowerCase();
      return (!q || a.name.toLowerCase().includes(q) || a.code.toLowerCase().includes(q))
        && (!filterClass  || a.assetClass === filterClass)
        && (!filterBucket || a.bucketId   === filterBucket);
    }), [assets, search, filterClass, filterBucket]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let av: number | string;
      let bv: number | string;
      switch (sortKey) {
        case 'name':       av = a.name;       bv = b.name;       break;
        case 'assetClass': av = a.assetClass; bv = b.assetClass; break;
        case 'bucket':     av = buckets.find(bk => bk.id === a.bucketId)?.name ?? ''; bv = buckets.find(bk => bk.id === b.bucketId)?.name ?? ''; break;
        case 'costPrice':  av = (a.costPrice ?? 0) * (a.quantity ?? 1); bv = (b.costPrice ?? 0) * (b.quantity ?? 1); break;
        case 'value':      av = getAssetCurrentValue(a);   bv = getAssetCurrentValue(b);   break;
        case 'gain':       av = getAssetUnrealizedGain(a); bv = getAssetUnrealizedGain(b); break;
        case 'income':       av = getAssetAnnualIncome(a);       bv = getAssetAnnualIncome(b);       break;
        case 'capgain':      av = getAssetCapitalGainIncome(a);  bv = getAssetCapitalGainIncome(b);  break;
        case 'totalreturn':  av = getAssetAnnualIncome(a) + getAssetCapitalGainIncome(a); bv = getAssetAnnualIncome(b) + getAssetCapitalGainIncome(b); break;
        default:             av = 0; bv = 0;
      }
      const cmp = typeof av === 'string' ? av.localeCompare(bv as string, 'vi') : (av as number) - (bv as number);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [filtered, sortKey, sortDir, buckets]);

  const f = (k: keyof Asset, v: any) => setForm(p => ({ ...p, [k]: v }));

  const calcAutoIncome = (price: number, qty: number, yr: number) =>
    Math.round((price * qty * yr) / 100);

  const openAdd = () => {
    const defaultClass = assetClasses[0];
    setForm({
      ...emptyAsset(),
      assetClass:   defaultClass?.name || 'Cổ phiếu',
      assetClassId: defaultClass?.id   || '',
    });
    setCodeEdited(false);
    setIncomeManuallySet(false);
    setAddOpen(true);
  };

  const openEdit = (a: Asset) => {
    const resolvedClassId = a.assetClassId
      || assetClasses.find(c => c.name === a.assetClass)?.id
      || '';
    setForm({ ...a, assetClassId: resolvedClassId, incomeEvents: [...(a.incomeEvents || [])] });
    setCodeEdited(true);
    setIncomeManuallySet(false);
    setPriceLinked(false);   // edit: giá hiện tại đã độc lập khỏi giá gốc
    setEditAsset(a);
  };
  const openSell = (a: Asset) => { setSellAsset(a); setSellForm({ quantity: 1, price: a.currentPrice, date: new Date().toISOString().split('T')[0], notes: '' }); };

  const handleSave = () => {
    if (!form.name || !form.code) return;

    // Promote slug-based assetClassId → UUID trước khi lưu
    let savedForm = { ...form };
    if (form.assetClassId && !isUUID(form.assetClassId)) {
      const slugCls = assetClasses.find(c => c.id === form.assetClassId);
      if (slugCls) {
        const existing = assetClasses.find(c => isUUID(c.id) && c.name === slugCls.name);
        if (existing) {
          savedForm = { ...savedForm, assetClassId: existing.id };
        } else {
          const newId = v4();
          const newClass: AssetClassMeta = { ...slugCls, id: newId };
          dispatch({ type: 'PROMOTE_ASSET_CLASS', payload: { oldId: slugCls.id, newClass } });
          savedForm = { ...savedForm, assetClassId: newId };
        }
      }
    }

    if (editAsset) {
      dispatch({ type: 'UPDATE_ASSET', payload: { ...editAsset, ...savedForm } as Asset });
      setEditAsset(null);
    } else {
      dispatch({ type: 'ADD_ASSET', payload: savedForm as Omit<Asset, 'id'> });
      setAddOpen(false);
    }
    setForm(emptyAsset());
    setPriceLinked(true);
  };

  const handleSell = () => {
    if (!sellAsset) return;
    dispatch({ type: 'SELL_ASSET', payload: { assetId: sellAsset.id, ...sellForm } });
    setSellAsset(null);
  };

  // ── Income Events editing ──
  const addIncomeEvent = () => {
    const ev: IncomeEvent = { id: v4(), month: 1, amount: 0, label: '' };
    f('incomeEvents', [...(form.incomeEvents || []), ev]);
  };

  const updateIncomeEvent = (id: string, key: keyof IncomeEvent, value: any) => {
    f('incomeEvents', (form.incomeEvents || []).map(ev => ev.id === id ? { ...ev, [key]: value } : ev));
  };

  const deleteIncomeEvent = (id: string) => {
    f('incomeEvents', (form.incomeEvents || []).filter(ev => ev.id !== id));
  };

  const autoFillSchedule = () => {
    if (!form.yieldRate || !form.costPrice || !form.quantity) return;
    const annual = (form.yieldRate / 100) * form.costPrice * form.quantity;
    const monthly = Math.round(annual / 12);
    const events: IncomeEvent[] = Array.from({ length: 12 }, (_, i) => ({
      id: v4(), month: i + 1, amount: monthly, label: `Thu nhập T${i + 1}`,
    }));
    f('incomeEvents', events);
  };

  const formAnnualIncome = (form.incomeEvents || []).reduce((s, e) => s + (e.amount || 0), 0);
  const totalValue = filtered.reduce((s, a) => s + getAssetCurrentValue(a), 0)
    + (walletAsset ? Math.max(0, walletAsset.currentPrice) : 0);
  const modalOpen = addOpen || editAsset !== null;
  const hasCompositeAssets = assets.some(a => a.isComposite);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <Search size={13} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--tx-4)' }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Tìm mã, tên tài sản..."
            style={{ width: '100%', paddingLeft: 34, paddingRight: 12, height: 36, borderRadius: 9, fontSize: 13, outline: 'none', backgroundColor: 'var(--bg-input)', border: '1px solid var(--bd)', color: 'var(--tx-1)' }} />
        </div>
        <select value={filterClass} onChange={e => setFilterClass(e.target.value)}
          style={{ height: 36, padding: '0 12px', borderRadius: 9, fontSize: 13, outline: 'none', cursor: 'pointer', backgroundColor: 'var(--bg-input)', border: '1px solid var(--bd)', color: filterClass ? 'var(--tx-1)' : 'var(--tx-3)' }}>
          <option value="">Tất cả lớp tài sản</option>
          {assetClasses.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
        </select>
        <select value={filterBucket} onChange={e => setFilterBucket(e.target.value)}
          style={{ height: 36, padding: '0 12px', borderRadius: 9, fontSize: 13, outline: 'none', cursor: 'pointer', backgroundColor: 'var(--bg-input)', border: '1px solid var(--bd)', color: filterBucket ? 'var(--tx-1)' : 'var(--tx-3)' }}>
          <option value="">Tất cả các quỹ</option>
          {buckets.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <Button variant="ghost" size="sm" onClick={() => setClassManagerOpen(true)}>Quản lý danh mục</Button>
        {/* Export */}
        <button
          onClick={handleExport}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            height: 34, padding: '0 14px', borderRadius: 9, fontSize: 12, fontWeight: 600,
            cursor: 'pointer', whiteSpace: 'nowrap',
            background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.3)',
            color: '#fbbf24', transition: 'all 0.15s',
          }}
        >
          <Download size={13} /> Xuất Excel
        </button>
        {/* Import */}
        <input ref={importRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleImportFile} />
        <button
          onClick={() => importRef.current?.click()}
          disabled={importing}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            height: 34, padding: '0 14px', borderRadius: 9, fontSize: 12, fontWeight: 600,
            cursor: importing ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap',
            background: 'rgba(56,189,248,0.08)', border: '1px solid rgba(56,189,248,0.3)',
            color: '#38bdf8', opacity: importing ? 0.6 : 1, transition: 'all 0.15s',
          }}
        >
          <Upload size={13} /> {importing ? 'Đang nhập...' : 'Nhập dữ liệu'}
        </button>
        {hasCompositeAssets && (
          <button
            onClick={handleRefreshAllHoldings}
            disabled={refreshingAll}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              height: 34, padding: '0 14px', borderRadius: 9, fontSize: 12, fontWeight: 600,
              cursor: refreshingAll ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap',
              background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.3)',
              color: '#10b981', opacity: refreshingAll ? 0.6 : 1, transition: 'all 0.15s',
            }}
          >
            <RefreshCw size={13} style={refreshingAll ? { animation: 'spin 1s linear infinite' } : undefined} />
            {refreshingAll ? 'Đang cập nhật...' : 'Cập nhật tất cả vị thế'}
          </button>
        )}
        <Button variant="primary" size="sm" icon={<Plus size={13} />} onClick={openAdd}>Thêm tài sản</Button>
      </div>

      {/* Import errors */}
      {importErrors.length > 0 && (
        <div style={{
          padding: '12px 16px', borderRadius: 10,
          background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.25)',
        }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#f87171', marginBottom: 6 }}>
            Lỗi khi nhập file ({importErrors.length} vấn đề)
          </div>
          {importErrors.map((msg, i) => (
            <div key={i} style={{ fontSize: 12, color: '#fca5a5', marginBottom: 3 }}>• {msg}</div>
          ))}
          <button
            onClick={() => setImportErrors([])}
            style={{ marginTop: 8, fontSize: 11, color: '#f87171', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
          >
            Đóng
          </button>
        </div>
      )}

      {/* Summary */}
      <div style={{ display: 'flex', gap: 20, padding: '11px 18px', borderRadius: 10, backgroundColor: 'var(--bg-card)', border: '1px solid var(--bd)' }}>
        <Stat label="Tổng giá trị" value={<Money value={totalValue} />} />
        <Sep />
        <Stat label="Số tài sản" value={filtered.length.toString()} />
        <Sep />
        <Stat label="Thu nhập / năm" value={<Money value={assets.reduce((s, a) => s + getAssetAnnualIncome(a), 0)} />} color="#10b981" />
      </div>

      {/* Table */}
      <Card>
        {/* Timestamp */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '10px 16px 0' }}>
          <span style={{ fontSize: 11, color: 'var(--tx-5)' }}>
            {syncedAt
              ? `Cập nhật lần cuối: ${syncedAt.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })} · ${syncedAt.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' })}`
              : 'Đang tải...'}
          </span>
        </div>
        <div className="table-wrap" ref={tableWrapRef}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--bd)' }}>
              <SortTh label="Tên tài sản"   col="name"       current={sortKey} dir={sortDir} onSort={handleSort} align="left" />
              <SortTh label="Lớp"           col="assetClass" current={sortKey} dir={sortDir} onSort={handleSort} align="left" />
              <SortTh label="Quỹ"           col="bucket"     current={sortKey} dir={sortDir} onSort={handleSort} align="left" />
              <SortTh label="Giá vốn"       col="costPrice"  current={sortKey} dir={sortDir} onSort={handleSort} align="right" />
              <SortTh label="Giá trị"       col="value"      current={sortKey} dir={sortDir} onSort={handleSort} align="right" />
              <SortTh label="Lãi / Lỗ"     col="gain"       current={sortKey} dir={sortDir} onSort={handleSort} align="right" />
              <SortTh label="Thu nhập/năm"  col="income"      current={sortKey} dir={sortDir} onSort={handleSort} align="right" />
              <SortTh label="Capital Gain"  col="capgain"     current={sortKey} dir={sortDir} onSort={handleSort} align="right" />
              <SortTh label="Tổng lợi suất" col="totalreturn" current={sortKey} dir={sortDir} onSort={handleSort} align="right" />
              <th style={TH('')}></th>
            </tr>
          </thead>
          <tbody>
            {/* ── Ví tiền mặt chung — pinned row ── */}
            {walletAsset && (
              <tr style={{ borderBottom: '2px solid rgba(167,139,250,0.2)', background: 'rgba(167,139,250,0.04)' }}>
                <td style={{ padding: '12px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 18 }}>🪙</span>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13, color: '#a78bfa' }}>{walletAsset.name}</div>
                      <div style={{ fontSize: 10, color: 'var(--tx-5)', marginTop: 1 }}>Tiền mặt khả dụng</div>
                    </div>
                  </div>
                </td>
                <td style={{ padding: '10px 16px' }}>
                  <span style={{ fontSize: 11, color: '#a78bfa', background: 'rgba(167,139,250,0.12)', padding: '3px 8px', borderRadius: 6, fontWeight: 500 }}>
                    Tiền mặt
                  </span>
                </td>
                <td style={{ padding: '10px 16px' }}>
                  <span style={{ fontSize: 11, color: 'var(--tx-4)' }}>Danh mục Chung</span>
                </td>
                <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                  <span style={{ fontSize: 12, color: 'var(--tx-5)' }}>—</span>
                </td>
                <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                  <SensitiveValue>
                    <div style={{ fontSize: 14, fontWeight: 700, color: walletAsset.currentPrice >= 0 ? '#a78bfa' : '#f87171' }}>
                      {formatVND(walletAsset.currentPrice)}
                    </div>
                  </SensitiveValue>
                </td>
                <td colSpan={4} style={{ padding: '10px 16px', textAlign: 'center' }}>
                  <span style={{ fontSize: 11, color: 'var(--tx-5)', fontStyle: 'italic' }}>Quản lý qua nhật ký giao dịch</span>
                </td>
                <td style={{ padding: '10px 16px' }}></td>
              </tr>
            )}
            {sorted.length === 0 && (
              <tr><td colSpan={10} style={{ padding: '48px 20px', textAlign: 'center', color: 'var(--tx-5)', fontSize: 13 }}>Chưa có tài sản nào · Nhấn "Thêm tài sản" để bắt đầu</td></tr>
            )}
            {sorted.map((a, i) => {
              const gain = getAssetUnrealizedGain(a);
              const pct = getAssetUnrealizedGainPercent(a);
              const pos = gain >= 0;
              const annualIncome = getAssetAnnualIncome(a);
              const totalCost = (a.costPrice ?? 0) * (a.quantity ?? 1);
              const assignedBucket = buckets.find(b => b.id === a.bucketId);
              const isUnallocated = !a.bucketId || !assignedBucket;
              const isExpanded = expandedHoldings === a.id;
              return (
                <React.Fragment key={a.id}>
                <tr
                  style={{ borderBottom: (!a.isComposite && i < sorted.length - 1) ? '1px solid var(--bd-sub)' : 'none', transition: 'background 0.1s' }}
                  onMouseEnter={e => { (e.currentTarget as HTMLTableRowElement).style.backgroundColor = 'var(--bg-row-hover)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLTableRowElement).style.backgroundColor = 'transparent'; }}
                >
                  <td style={{ padding: '13px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {a.isComposite && (
                        <button
                          onClick={() => setExpandedHoldings(isExpanded ? null : a.id)}
                          title={isExpanded ? 'Thu gọn vị thế' : 'Xem vị thế'}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', color: isExpanded ? 'var(--accent)' : 'var(--tx-4)', flexShrink: 0 }}
                        >
                          <Layers size={13} />
                        </button>
                      )}
                      <div
                        onClick={() => openEdit(a)}
                        style={{ fontWeight: 600, fontSize: 14, color: 'var(--tx-1)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer' }}
                        onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.color = 'var(--accent)'; (e.currentTarget as HTMLDivElement).style.textDecoration = 'underline'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.color = 'var(--tx-1)'; (e.currentTarget as HTMLDivElement).style.textDecoration = 'none'; }}
                      >{a.name}</div>
                    </div>
                  </td>
                  {/* ── Cột Loại tài sản — inline select ── */}
                  <td style={{ padding: '10px 16px' }}>
                    <select
                      value={a.assetClassId || ''}
                      onChange={e => handleQuickAssignClass(a, e.target.value)}
                      onClick={e => e.stopPropagation()}
                      style={{
                        fontSize: 11, padding: '4px 8px', borderRadius: 7, cursor: 'pointer',
                        border: `1px solid ${resolveClassColor(a.assetClass, assetClasses)}40`,
                        background: `${resolveClassColor(a.assetClass, assetClasses)}12`,
                        color: resolveClassColor(a.assetClass, assetClasses),
                        fontWeight: 500, outline: 'none', maxWidth: 140,
                        overflow: 'hidden', textOverflow: 'ellipsis',
                      }}
                    >
                      {!a.assetClassId && (
                        <option value="" disabled style={{ background: 'var(--bg-card)', color: 'var(--tx-4)' }}>
                          {a.assetClass || '— Chưa phân loại'}
                        </option>
                      )}
                      {assetClasses.map(c => (
                        <option key={c.id} value={c.id} style={{ background: 'var(--bg-card)', color: 'var(--tx-1)' }}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  {/* ── Cột Quỹ — inline select ── */}
                  <td style={{ padding: '10px 16px' }}>
                    <select
                      value={a.bucketId || ''}
                      onChange={e => handleQuickAssign(a, e.target.value)}
                      onClick={e => e.stopPropagation()}
                      style={{
                        fontSize: 11, padding: '4px 8px', borderRadius: 7, cursor: 'pointer',
                        border: `1px solid ${isUnallocated ? 'var(--bd)' : `${assignedBucket?.color ?? '#10b981'}40`}`,
                        background: isUnallocated ? 'var(--bg-card-el)' : `${assignedBucket?.color ?? '#10b981'}12`,
                        color: isUnallocated ? 'var(--tx-4)' : (assignedBucket?.color ?? '#10b981'),
                        fontWeight: 500, outline: 'none', maxWidth: 130,
                        overflow: 'hidden', textOverflow: 'ellipsis',
                      }}
                    >
                      {isUnallocated && (
                        <option value="" disabled style={{ background: 'var(--bg-card)', color: 'var(--tx-4)' }}>
                          — Chưa phân loại
                        </option>
                      )}
                      {buckets.map(b => (
                        <option key={b.id} value={b.id} style={{ background: 'var(--bg-card)', color: 'var(--tx-1)' }}>
                          {b.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td style={{ padding: '13px 16px', textAlign: 'right', fontSize: 12, color: 'var(--tx-3)', whiteSpace: 'nowrap' }}><SensitiveValue>{formatVND(totalCost)}</SensitiveValue></td>
                  <td style={{ padding: '13px 16px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--tx-1)', fontVariantNumeric: 'tabular-nums' }}><SensitiveValue>{formatVND(getAssetCurrentValue(a))}</SensitiveValue></span>
                  </td>
                  <td style={{ padding: '13px 16px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: pos ? '#10b981' : '#f87171' }}><SensitiveValue>{pos ? '+' : ''}{formatVND(gain)}</SensitiveValue></div>
                    <div style={{ fontSize: 11, color: pos ? '#059669' : '#dc2626', marginTop: 2, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 3 }}>
                      {pos ? <ArrowUpRight size={10} /> : <ArrowDownRight size={10} />}{Math.abs(pct).toFixed(2)}%
                    </div>
                  </td>
                  <td style={{ padding: '13px 16px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {annualIncome > 0 ? (
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#10b981' }}><SensitiveValue>{formatVND(annualIncome)}</SensitiveValue></div>
                        {a.yieldRate ? <div style={{ fontSize: 10, color: '#10b981', marginTop: 1 }}>{a.yieldRate}% / năm</div> : null}
                      </div>
                    ) : <span style={{ color: 'var(--tx-5)' }}>—</span>}
                  </td>
                  {/* ── Capital Gain ── */}
                  {(() => {
                    const capGainIncome = getAssetCapitalGainIncome(a);
                    const capGainRate   = a.capitalGainRate ?? 0;
                    return (
                      <td style={{ padding: '13px 16px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {capGainRate > 0 ? (
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#38bdf8' }}><SensitiveValue>{formatVND(capGainIncome)}</SensitiveValue></div>
                            <div style={{ fontSize: 10, color: '#38bdf8', marginTop: 1 }}>{capGainRate.toFixed(1)}% / năm</div>
                          </div>
                        ) : <span style={{ color: 'var(--tx-5)' }}>—</span>}
                      </td>
                    );
                  })()}
                  {/* ── Tổng lợi suất ── */}
                  {(() => {
                    const capGainIncome    = getAssetCapitalGainIncome(a);
                    const totalReturnRate  = (a.yieldRate ?? 0) + (a.capitalGainRate ?? 0);
                    const totalReturnAmt   = annualIncome + capGainIncome;
                    const isHighGrowth     = totalReturnRate > 15;
                    return (
                      <td style={{ padding: '13px 16px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {totalReturnRate > 0 ? (
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: isHighGrowth ? '#059669' : '#10b981' }}><SensitiveValue>{formatVND(totalReturnAmt)}</SensitiveValue></div>
                            {isHighGrowth ? (
                              <span style={{
                                display: 'inline-block', marginTop: 2,
                                fontSize: 10, fontWeight: 700, color: '#059669',
                                padding: '1px 5px', borderRadius: 4,
                                background: 'rgba(5,150,105,0.14)',
                              }}>
                                ★ {totalReturnRate.toFixed(1)}%
                              </span>
                            ) : (
                              <div style={{ fontSize: 10, fontWeight: 400, marginTop: 2, color: 'var(--tx-4)' }}>
                                {totalReturnRate.toFixed(1)}%
                              </div>
                            )}
                          </div>
                        ) : <span style={{ color: 'var(--tx-5)' }}>—</span>}
                      </td>
                    );
                  })()}
                  <td style={{ padding: '13px 16px' }}>
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                      {a.isComposite && (
                        <IBtn
                          icon={<RefreshCw size={12} style={refreshingAsset === a.id ? { animation: 'spin 1s linear infinite' } : undefined} />}
                          title="Cập nhật giá vị thế"
                          color="#10b981"
                          onClick={() => handleRefreshAssetHoldings(a.id)}
                        />
                      )}
                      <IBtn icon={<Edit2 size={12} />} title="Sửa" color="#64748b" onClick={() => openEdit(a)} />
                      <IBtn icon={<TrendingDown size={12} />} title="Bán" color="#fbbf24" onClick={() => openSell(a)} />
                      <IBtn icon={<Trash2 size={12} />} title="Xóa" color="#f87171" onClick={() => { if (window.confirm('Xóa tài sản này?')) dispatch({ type: 'DELETE_ASSET', payload: a.id }); }} />
                    </div>
                  </td>
                </tr>
                {a.isComposite && isExpanded && (
                  <tr style={{ borderBottom: i < sorted.length - 1 ? '1px solid var(--bd-sub)' : 'none' }}>
                    <td colSpan={10} style={{ padding: '0 16px 16px 32px', backgroundColor: 'rgba(16,185,129,0.03)' }}>
                      <HoldingsTable
                        assetId={a.id}
                        onRefreshPrices={() => handleRefreshAssetHoldings(a.id)}
                        refreshing={refreshingAsset === a.id}
                      />
                    </td>
                  </tr>
                )}
                </React.Fragment>
              );
            })}
          </tbody>
          {/* ── Footer / Summary Row ── */}
          {sorted.length > 0 && (() => {
            const totCost      = sorted.reduce((s, a) => s + (a.costPrice ?? 0) * (a.quantity ?? 1), 0);
            const totVal       = sorted.reduce((s, a) => s + getAssetCurrentValue(a), 0);
            const totGain      = sorted.reduce((s, a) => s + getAssetUnrealizedGain(a), 0);
            const totIncome    = sorted.reduce((s, a) => s + getAssetAnnualIncome(a), 0);
            const totCapGain   = sorted.reduce((s, a) => s + getAssetCapitalGainIncome(a), 0);
            const totReturn    = totIncome + totCapGain;
            const wtdPct       = totCost > 0 ? (totGain / totCost) * 100 : 0;
            const totReturnPct = totVal  > 0 ? (totReturn / totVal)  * 100 : 0;
            const pos          = totGain >= 0;
            return (
              <tfoot>
                <tr style={{ borderTop: '2px solid var(--bd)', backgroundColor: 'rgba(16,185,129,0.04)' }}>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--tx-3)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Tổng cộng</span>
                    <div style={{ fontSize: 10, color: 'var(--tx-5)', marginTop: 1 }}>{sorted.length} tài sản</div>
                  </td>
                  <td /><td />
                  <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 13, fontWeight: 700, color: 'var(--tx-2)' }}><SensitiveValue>{formatVND(totCost)}</SensitiveValue></td>
                  <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 15, fontWeight: 700, color: 'var(--tx-1)' }}><SensitiveValue>{formatVND(totVal)}</SensitiveValue></td>
                  <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: pos ? '#10b981' : '#f87171' }}><SensitiveValue>{pos ? '+' : ''}{formatVND(totGain)}</SensitiveValue></div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: pos ? '#059669' : '#dc2626', marginTop: 2, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 3 }}>
                      {pos ? <ArrowUpRight size={10} /> : <ArrowDownRight size={10} />}{Math.abs(wtdPct).toFixed(2)}% tb. gia quyền
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 13, fontWeight: 700, color: totIncome > 0 ? '#10b981' : 'var(--tx-5)' }}>
                    {totIncome > 0 ? <SensitiveValue>{formatVND(totIncome)}</SensitiveValue> : '—'}
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 13, fontWeight: 700, color: totCapGain > 0 ? '#38bdf8' : 'var(--tx-5)' }}>
                    {totCapGain > 0 ? <SensitiveValue>{formatVND(totCapGain)}</SensitiveValue> : '—'}
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                    {totReturn > 0 ? (
                      <>
                        <div style={{ fontSize: 13, fontWeight: 700, color: totReturnPct > 15 ? '#059669' : '#10b981' }}><SensitiveValue>{formatVND(totReturn)}</SensitiveValue></div>
                        <div style={{ fontSize: 10, fontWeight: totReturnPct > 15 ? 700 : 400, marginTop: 2, color: totReturnPct > 15 ? '#059669' : 'var(--tx-4)' }}>
                          {totReturnPct > 15 && '★ '}{totReturnPct.toFixed(1)}% tb.
                        </div>
                      </>
                    ) : <span style={{ color: 'var(--tx-5)' }}>—</span>}
                  </td>
                  <td />
                </tr>
              </tfoot>
            );
          })()}
        </table>
        </div>
      </Card>

      {/* ── Add / Edit Modal ── */}
      <Modal open={modalOpen} onClose={() => { setAddOpen(false); setEditAsset(null); setForm(emptyAsset()); setScheduleOpen(false); }}
        title={editAsset ? 'Chỉnh sửa tài sản' : 'Thêm tài sản mới'} subtitle="Thông tin cơ bản & lịch dòng tiền" size="xl">

        {/* Basic info */}
        <div className="rsp-modal-grid">
          <Input label="Tên tài sản *" value={form.name || ''} onChange={e => {
            const name = e.target.value;
            f('name', name);
            if (!codeEdited) f('code', generateIdFromName(name));
          }} placeholder="VD: Căn hộ Quận 7, VNM, Vàng SJC..." />
          <Input label="Mã tài sản *" value={form.code || ''} onChange={e => { setCodeEdited(true); f('code', e.target.value); }}
            placeholder="VD: can-ho-q7" hint="Tự động sinh từ tên · có thể chỉnh sửa" />
          <Select label="Lớp tài sản" value={form.assetClassId || ''} onChange={e => {
            const selected = assetClasses.find(c => c.id === e.target.value);
            if (selected) { f('assetClassId', selected.id); f('assetClass', selected.name); }
          }} options={assetClasses.map(c => ({ value: c.id, label: c.name }))} />
          <Input label="Ngày mua" type="date" value={form.purchaseDate || ''} onChange={e => f('purchaseDate', e.target.value)} />
          {editAsset ? (
            /* Chế độ sửa: khoá giá vốn — chỉ thay đổi qua giao dịch */
            <div style={{ marginBottom: 4 }}>
              <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--tx-4)', textTransform: 'uppercase', letterSpacing: '0.03em', display: 'flex', alignItems: 'center', gap: 5 }}>
                Giá vốn
                <span style={{ fontSize: 10, color: '#fbbf24', background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.25)', borderRadius: 5, padding: '1px 6px', fontWeight: 600, letterSpacing: 0 }}>🔒 Chỉ đọc</span>
              </label>
              <div style={{ marginTop: 6, padding: '10px 12px', borderRadius: 10, background: 'var(--bg-card-el)', border: '1px solid var(--bd)', fontSize: 13, color: 'var(--tx-2)', fontWeight: 600 }}>
                {formatVND(form.costPrice || 0)}
              </div>
              <div style={{ fontSize: 11, color: 'var(--tx-5)', marginTop: 4 }}>
                Sửa giá vốn bằng cách chỉnh sửa <b>Giao dịch gốc</b> trong lịch sử bên dưới
              </div>
            </div>
          ) : (
            <Input label="Giá vốn (VNĐ/đơn vị)" type="number" value={form.costPrice || ''} onChange={e => {
              const cost = parseFloat(e.target.value) || 0;
              setForm(p => ({
                ...p,
                costPrice:    cost,
                currentPrice: priceLinked ? cost : (p.currentPrice ?? 0),
              }));
            }} hint="BĐS: nhập tổng giá mua, số lượng = 1" />
          )}
          <Input label="Số lượng" type="number" value={form.quantity || ''} onChange={e => {
            const qty = parseFloat(e.target.value) || 0;
            setForm(p => {
              const next = { ...p, quantity: qty };
              if (!incomeManuallySet) next.estimatedIncome = calcAutoIncome(next.currentPrice ?? 0, qty, next.yieldRate ?? 0);
              return next;
            });
          }} />
          <div style={{ position: 'relative' }}>
            {priceLinked && (
              <div style={{
                position: 'absolute', top: 0, right: 0, zIndex: 1,
                fontSize: 10, fontWeight: 600, letterSpacing: '0.03em',
                color: '#60a5fa', background: 'rgba(96,165,250,0.12)',
                border: '1px solid rgba(96,165,250,0.25)',
                borderRadius: '0 8px 0 8px', padding: '2px 7px',
              }}>= giá gốc</div>
            )}
          <Input label="Giá hiện tại (VNĐ/đơn vị)" type="number" value={form.currentPrice || ''}
            style={priceLinked ? { background: 'rgba(96,165,250,0.06)', borderColor: 'rgba(96,165,250,0.3)' } : undefined}
            onChange={e => {
            const price = parseFloat(e.target.value) || 0;
            setPriceLinked(false);
            setForm(p => {
              const next = { ...p, currentPrice: price };
              if (!incomeManuallySet) next.estimatedIncome = calcAutoIncome(price, next.quantity ?? 1, next.yieldRate ?? 0);
              return next;
            });
          }} />
          </div>
          <Input label="Tỷ suất sinh lời (%/năm)" type="number" value={form.yieldRate || ''} onChange={e => {
            const yr = parseFloat(e.target.value) || 0;
            setForm(p => {
              const next = { ...p, yieldRate: yr };
              if (!incomeManuallySet) next.estimatedIncome = calcAutoIncome(next.currentPrice ?? 0, next.quantity ?? 1, yr);
              return next;
            });
          }} hint="Tỷ suất sinh lời hàng năm" />
          <Input label="Tăng giá (%/năm)" type="number" value={form.capitalGainRate || ''} onChange={e => f('capitalGainRate', parseFloat(e.target.value) || 0)} hint="Tỷ lệ tăng giá vốn dự phóng hàng năm" />
          {/* Tổng tỷ suất — read-only auto-calc */}
          <div>
            <div style={labelStyle}>Tổng tỷ suất (%/năm)</div>
            <div style={{ padding: '9px 12px', borderRadius: 9, fontSize: 14, fontWeight: 700, backgroundColor: 'var(--bg-card-el)', border: '1px solid var(--bd)', color: '#10b981' }}>
              {((form.yieldRate || 0) + (form.capitalGainRate || 0)).toFixed(2)}%
              <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--tx-4)', marginLeft: 8 }}>
                = {(form.yieldRate || 0).toFixed(1)}% Yield + {(form.capitalGainRate || 0).toFixed(1)}% Tăng giá
              </span>
            </div>
          </div>
          {/* Thu nhập / Năm — span 2 cols */}
          <div style={{ gridColumn: 'span 2' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={labelStyle}>Thu nhập / Năm (ước tính)</span>
              {incomeManuallySet ? (
                <>
                  <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 5, background: 'rgba(251,191,36,0.12)', color: '#fbbf24', fontWeight: 500 }}>Thủ công</span>
                  <button onClick={() => {
                    setIncomeManuallySet(false);
                    const auto = calcAutoIncome(form.currentPrice ?? 0, form.quantity ?? 1, form.yieldRate ?? 0);
                    f('estimatedIncome', auto);
                  }} style={{ fontSize: 11, color: 'var(--tx-4)', cursor: 'pointer', background: 'none', border: 'none', padding: 0, display: 'flex', alignItems: 'center', gap: 3, textDecoration: 'underline' }}>
                    ↺ Đặt lại tự động
                  </button>
                </>
              ) : (
                <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 5, background: 'rgba(16,185,129,0.1)', color: '#10b981', fontWeight: 500 }}>Tự động</span>
              )}
            </div>
            <input type="number" value={form.estimatedIncome || ''} placeholder="0"
              onChange={e => { setIncomeManuallySet(true); f('estimatedIncome', parseFloat(e.target.value) || 0); }}
              style={{ width: '100%', padding: '9px 12px', borderRadius: 9, fontSize: 13, outline: 'none', backgroundColor: 'var(--bg-input)', border: '1px solid var(--bd)', color: 'var(--tx-1)', fontVariantNumeric: 'tabular-nums', boxSizing: 'border-box' }} />
          </div>
          {assetClasses.find(c => c.id === form.assetClassId)?.isFixedIncome && <>
            <Input label="Lãi suất (%/năm)" type="number" value={form.interestRate || ''} onChange={e => f('interestRate', parseFloat(e.target.value) || 0)} />
            <Input label="Ngày đáo hạn" type="date" value={form.maturityDate || ''} onChange={e => f('maturityDate', e.target.value)} />
          </>}
        </div>

        {/* Bucket assignment — single selection (radio) */}
        <div style={{ marginTop: 16 }}>
          <div style={labelStyle}>Gán vào Quỹ <span style={{ color: 'var(--tx-4)', fontSize: 10, fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(chọn 1)</span></div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
            {buckets.map(b => {
              const sel = form.bucketId === b.id;
              return (
                <button key={b.id} onClick={() => f('bucketId', b.id)}
                  style={{
                    padding: '6px 13px', borderRadius: 8, fontSize: 12, cursor: 'pointer', fontWeight: 500,
                    transition: 'all 0.12s', display: 'flex', alignItems: 'center', gap: 6,
                    background: sel ? `${b.color}18` : 'var(--bg-card-el)',
                    color: sel ? b.color : 'var(--tx-3)',
                    border: `1px solid ${sel ? `${b.color}35` : 'var(--bd)'}`,
                  }}>
                  {/* Radio dot */}
                  <span style={{
                    width: 10, height: 10, borderRadius: '50%', flexShrink: 0, display: 'inline-block',
                    border: `2px solid ${sel ? b.color : 'var(--tx-4)'}`,
                    background: sel ? b.color : 'transparent',
                    transition: 'all 0.12s',
                  }} />
                  {b.name}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Income Schedule ── */}
        <div style={{ marginTop: 18, borderRadius: 12, border: '1px solid rgba(16,185,129,0.15)', overflow: 'hidden' }}>
          {/* Header toggle */}
          <button
            onClick={() => setScheduleOpen(v => !v)}
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 16px', background: 'rgba(16,185,129,0.06)', border: 'none', cursor: 'pointer' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <CalendarDays size={15} color="#10b981" />
              <span style={{ fontSize: 13, fontWeight: 600, color: '#10b981' }}>Lịch dòng tiền (Income Schedule)</span>
              {(form.incomeEvents || []).length > 0 && (
                <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 5, background: 'rgba(16,185,129,0.15)', color: '#10b981' }}>
                  {(form.incomeEvents || []).length} mốc · <Money value={formAnnualIncome} />/năm
                </span>
              )}
            </div>
            {scheduleOpen ? <ChevronUp size={14} color="var(--tx-4)" /> : <ChevronDown size={14} color="var(--tx-4)" />}
          </button>

          {scheduleOpen && (
            <div style={{ padding: '16px' }}>
              {/* Auto-fill helper */}
              {(form.yieldRate || 0) > 0 && (form.costPrice || 0) > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 9, background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.12)', marginBottom: 14 }}>
                  <div style={{ flex: 1, fontSize: 12, color: 'var(--tx-3)' }}>
                    Thu nhập ước tính: <strong style={{ color: '#10b981' }}><Money value={((form.yieldRate || 0) / 100) * (form.costPrice || 0) * (form.quantity || 1)} /></strong>/năm từ {form.yieldRate}%
                  </div>
                  <Button variant="secondary" size="xs" onClick={autoFillSchedule}>Tự động phân bổ đều 12 tháng</Button>
                </div>
              )}

              {/* Events table */}
              {(form.incomeEvents || []).length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 2fr auto', gap: 8, marginBottom: 6 }}>
                    {['Tháng', 'Số tiền (VNĐ)', 'Nhãn', ''].map(h => (
                      <div key={h} style={{ fontSize: 10, color: 'var(--tx-4)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', padding: '0 4px' }}>{h}</div>
                    ))}
                  </div>
                  {(form.incomeEvents || []).map(ev => (
                    <div key={ev.id} style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 2fr auto', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                      <select value={ev.month} onChange={e => updateIncomeEvent(ev.id, 'month', parseInt(e.target.value))}
                        style={{ padding: '7px 8px', borderRadius: 8, fontSize: 12, outline: 'none', cursor: 'pointer', background: 'var(--bg-input)', border: '1px solid var(--bd)', color: 'var(--tx-1)' }}>
                        {MONTHS_VI.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                      </select>
                      <input type="number" value={ev.amount || ''} onChange={e => updateIncomeEvent(ev.id, 'amount', parseFloat(e.target.value) || 0)}
                        style={{ padding: '7px 10px', borderRadius: 8, fontSize: 12, outline: 'none', background: 'var(--bg-input)', border: '1px solid var(--bd)', color: 'var(--tx-1)' }} />
                      <input value={ev.label} onChange={e => updateIncomeEvent(ev.id, 'label', e.target.value)} placeholder="VD: Cổ tức Q2"
                        style={{ padding: '7px 10px', borderRadius: 8, fontSize: 12, outline: 'none', background: 'var(--bg-input)', border: '1px solid var(--bd)', color: 'var(--tx-1)' }} />
                      <button onClick={() => deleteIncomeEvent(ev.id)} style={{ width: 28, height: 28, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: 'rgba(239,68,68,0.08)', border: 'none', color: '#f87171' }}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ))}
                  {/* Annual total */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '8px 0 0', borderTop: '1px solid var(--bd)', marginTop: 4 }}>
                    <span style={{ fontSize: 12, color: 'var(--tx-3)' }}>Tổng / năm: </span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#10b981', marginLeft: 8 }}><Money value={formAnnualIncome} /></span>
                  </div>
                </div>
              )}

              <Button variant="secondary" size="xs" icon={<Plus size={12} />} onClick={addIncomeEvent}>Thêm mốc thu nhập</Button>
            </div>
          )}
        </div>

        <div style={{ marginTop: 14 }}>
          <Textarea label="Ghi chú" value={form.notes || ''} onChange={e => f('notes', e.target.value)} />
        </div>

        {/* Composite asset toggle */}
        <div style={{ marginTop: 14, padding: '12px 14px', borderRadius: 10, border: '1px solid var(--bd)', background: 'rgba(0,209,255,0.03)' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={!!form.isComposite}
              onChange={e => {
                f('isComposite', e.target.checked);
                // Pre-assign UUID so HoldingsTable can work immediately for new assets
                if (e.target.checked && !editAsset && !form.id) f('id', v4());
              }}
              style={{ width: 15, height: 15, cursor: 'pointer', accentColor: '#00D1FF' }}
            />
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-2)' }}>
                Danh mục tổng hợp (có nhiều vị thế)
              </div>
              <div style={{ fontSize: 11, color: 'var(--tx-5)', marginTop: 2 }}>
                {form.isComposite
                  ? 'Giá trị sẽ được tính tự động từ tổng các vị thế bên trong.'
                  : 'Tích chọn nếu tài sản này chứa nhiều mã chứng khoán, crypto, v.v.'}
              </div>
            </div>
          </label>
        </div>

        {/* ── Quản lý vị thế — hiện ngay trong modal khi isComposite ── */}
        {form.isComposite && (editAsset?.id || form.id) && (
          <div style={{ marginTop: 4 }}>
            <HoldingsTable
              assetId={(editAsset?.id || form.id)!}
              onRefreshPrices={async () => {
                const aid = editAsset?.id || form.id;
                if (!aid) return;
                await handleRefreshAssetHoldings(aid);
              }}
              refreshing={refreshingAsset === (editAsset?.id || form.id)}
            />
          </div>
        )}

        {editAsset && (
          <div style={{ marginTop: 14 }}>
            <AssetTransactionHistory
              asset={{ ...editAsset, ...form } as Asset}
              onNavigateToTx={onNavigateToTx ? (txId) => {
                setAddOpen(false); setEditAsset(null); setForm(emptyAsset());
                onNavigateToTx(txId);
              } : undefined}
            />
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--bd)' }}>
          <Button variant="ghost" onClick={() => { setAddOpen(false); setEditAsset(null); }}>Hủy</Button>
          <Button variant="primary" onClick={handleSave}>{editAsset ? 'Lưu thay đổi' : 'Thêm tài sản'}</Button>
        </div>
      </Modal>

      {/* Sell Modal */}
      <Modal open={sellAsset !== null} onClose={() => setSellAsset(null)} title={`Bán · ${sellAsset?.name}`} size="sm">
        {sellAsset && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, padding: '12px 14px', borderRadius: 10, background: 'var(--bg-card-el)', border: '1px solid var(--bd)' }}>
              <div><div style={{ fontSize: 10, color: 'var(--tx-4)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Đang nắm giữ</div><div style={{ fontSize: 16, fontWeight: 700, color: 'var(--tx-1)', marginTop: 3 }}>{formatNumber(sellAsset.quantity)}</div></div>
              <div><div style={{ fontSize: 10, color: 'var(--tx-4)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Giá vốn</div><div style={{ fontSize: 14, fontWeight: 600, color: 'var(--tx-2)', marginTop: 3 }}><Money value={sellAsset.costPrice} /></div></div>
            </div>
            <Input label="Số lượng bán" type="number" value={sellForm.quantity} onChange={e => setSellForm(f => ({ ...f, quantity: parseFloat(e.target.value) || 0 }))} hint={`Tối đa: ${sellAsset.quantity}`} />
            <Input label="Giá bán (VNĐ/đơn vị)" type="number" value={sellForm.price} onChange={e => setSellForm(f => ({ ...f, price: parseFloat(e.target.value) || 0 }))} />
            <Input label="Ngày bán" type="date" value={sellForm.date} onChange={e => setSellForm(f => ({ ...f, date: e.target.value }))} />
            {sellForm.quantity > 0 && sellForm.price > 0 && (
              <div style={{ padding: '12px 14px', borderRadius: 10, background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.15)' }}>
                <Row label="Tổng thu về" value={<Money value={sellForm.quantity * sellForm.price} />} valueColor="#e2e8f0" />
                <Row label="Lãi / lỗ ước tính" value={<Money value={(sellForm.price - sellAsset.costPrice) * sellForm.quantity} prefix={(sellForm.price - sellAsset.costPrice) >= 0 ? '+' : ''} />} valueColor={(sellForm.price - sellAsset.costPrice) >= 0 ? '#10b981' : '#f87171'} />
              </div>
            )}
            <Input label="Ghi chú" value={sellForm.notes} onChange={e => setSellForm(f => ({ ...f, notes: e.target.value }))} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 14, borderTop: '1px solid var(--bd)' }}>
              <Button variant="ghost" onClick={() => setSellAsset(null)}>Hủy</Button>
              <Button variant="danger" onClick={handleSell} disabled={sellForm.quantity <= 0 || sellForm.quantity > sellAsset.quantity}>Xác nhận bán</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Asset Class Manager Modal */}
      <AssetClassManager open={classManagerOpen} onClose={() => setClassManagerOpen(false)} />

      {/* Toast */}
      {toast && <Toast message={toast} />}
    </div>
  );
}

// ── Micro components ──────────────────────────────────────────────────────────
function IBtn({ icon, title, color, onClick }: { icon: React.ReactNode; title: string; color: string; onClick: () => void }) {
  return (
    <button title={title} onClick={onClick}
      style={{ width: 28, height: 28, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', border: 'none', background: 'var(--bg-card-el)', color, transition: 'background 0.1s' }}
      onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-row-hover)'; }}
      onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-card-el)'; }}>
      {icon}
    </button>
  );
}

function Stat({ label, value, color }: { label: string; value: React.ReactNode; color?: string }) {
  return (
    <div>
      <span style={{ fontSize: 11, color: 'var(--tx-4)' }}>{label} </span>
      <span style={{ fontSize: 13, fontWeight: 700, color: color || 'var(--tx-1)' }}>{value}</span>
    </div>
  );
}

function Sep() {
  return <div style={{ width: 1, backgroundColor: 'var(--bd)', alignSelf: 'stretch' }} />;
}

function Row({ label, value, valueColor }: { label: string; value: React.ReactNode; valueColor: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
      <span style={{ fontSize: 12, color: 'var(--tx-3)' }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 700, color: valueColor }}>{value}</span>
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  fontSize: 11, color: 'var(--tx-4)', fontWeight: 500,
  letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 8,
};

function Toast({ message }: { message: string }) {
  return (
    <div style={{
      position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
      padding: '11px 18px', borderRadius: 12,
      background: 'var(--bg-card)',
      border: '1px solid rgba(16,185,129,0.3)',
      boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
      display: 'flex', alignItems: 'center', gap: 10,
      fontSize: 13, color: 'var(--tx-1)', fontWeight: 500,
      animation: 'toastIn 0.2s ease',
      maxWidth: 340,
    }}>
      <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#10b981', flexShrink: 0, boxShadow: '0 0 6px #10b981' }} />
      {message}
    </div>
  );
}

function SortTh({ label, col, current, dir, onSort, align = 'left' }: {
  label: string; col: string; current: string; dir: 'asc' | 'desc';
  onSort: (k: any) => void; align?: 'left' | 'right';
}) {
  const active = current === col;
  const Icon = active ? (dir === 'asc' ? ArrowUp : ArrowDown) : ChevronsUpDown;
  return (
    <th style={{ ...TH(label, align), cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
      onClick={() => onSort(col)}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: active ? 'var(--tx-2)' : 'var(--tx-4)' }}>
        {align === 'right' && <Icon size={11} style={{ opacity: active ? 1 : 0.4 }} />}
        {label}
        {align === 'left'  && <Icon size={11} style={{ opacity: active ? 1 : 0.4 }} />}
      </span>
    </th>
  );
}
